import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useTabActive } from "../../lib/tabActive";
import { useIsMobile } from "../../lib/useIsMobile";
import { Icon } from "./Icon";
import "./SearchSelect.css";

export interface SSOption {
  value: string;
  label: string;
  desc?: string;
  tail?: string;
  icon?: string;
}
export interface SSGroup {
  group: string;
  items: SSOption[];
}
type SSInput = SSOption | SSGroup;

type FlatItem = { type: "item" } & SSOption;
type FlatGroup = { type: "group"; label: string };
type Flat = FlatItem | FlatGroup;

interface Props {
  value: string | null;
  options: SSInput[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  icon?: string;
  disabled?: boolean;
  error?: boolean;
  clearable?: boolean;
  emptyText?: string;
  showFooter?: boolean;
  id?: string;
  /** Her seçenek satırında gösterilen opsiyonel eylem (örn. düzenle) — seçim yapmadan tetiklenir. */
  itemAction?: { icon: string; label: string; onAction: (value: string) => void };
  /** Açılışta arama kutusuna önceden yazılan terim (dış filtre devri). Kullanıcı revize/temizleyebilir. */
  seedQuery?: string;
  /**
   * ÇOK SEÇİM MODU. `multiValues` VERİLİRSE bileşen çoklu moda geçer: `value`/`onChange`
   * yok sayılır, seçim `onChangeMulti` ile bildirilir ve panel seçimde KAPANMAZ.
   * Verilmezse davranış birebir eskisi gibidir — mevcut ~30 çağrı yeri etkilenmez.
   */
  multiValues?: string[];
  onChangeMulti?: (values: string[]) => void;
  /** Çoklu modda tetikleyicide gösterilecek özet ("3 müşteri seçili" gibi). */
  multiSummary?: (count: number) => string;
}

function normalize(o: SSOption | string): SSOption {
  return typeof o === "string" ? { value: o, label: o } : o;
}
function isGroup(o: SSInput): o is SSGroup {
  return Array.isArray((o as SSGroup).items) && (o as SSGroup).group != null;
}

/**
 * Aranabilir select (Select2-tarzı). GENTAŞ design_source SearchSelect port'u.
 * Klavye: ↑↓ gez · ↵ seç · esc kapat. ESC + dış-tık ile kapanır.
 */
export function SearchSelect({
  value, options, onChange,
  placeholder = "Seç…", searchPlaceholder = "Ara…",
  icon, disabled, error, clearable = true,
  emptyText = "Sonuç bulunamadı", showFooter = true, id,
  itemAction, seedQuery,
  multiValues, onChangeMulti, multiSummary,
}: Props) {
  const cok = multiValues != null; // çoklu mod açık mı
  const secililer = useMemo(() => new Set(multiValues ?? []), [multiValues]);
  const [open, setOpen] = useState(false);
  const tabActive = useTabActive();
  /**
   * MOBİLDE PANEL GÖRÜNÜR ALANA OTURUR (tetikleyiciye DEĞİL).
   * Masaüstünde panel tetikleyicinin altına/üstüne açılır. Telefonda bu iki yerden kırılıyordu:
   * (1) yükseklik `window.innerHeight`ten hesaplanıyordu — iOS'ta klavye açılınca innerHeight
   *     KÜÇÜLMEZ, resize da gelmez; arama kutusu odaklanınca açılan klavye listenin alt yarısını
   *     örtüyor, 5 sonuçlu bir aramada son 3 sonuç klavye kapatılmadan HİÇ görünmüyordu;
   * (2) panel tetikleyicinin genişliğini birebir alıyordu — dar bir hücrede dar, kısmen ekran
   *     dışındaki bir tetikleyicide ekran dışında (panel `fixed`, oraya kaydırılamaz).
   * Mobilde panel görünür alanın (`visualViewport`) üstüne, kenardan kenara yerleşir ve
   * yüksekliğini klavye düşülmüş alandan alır: arama üstte, klavye altta, liste arada.
   */
  const mobil = useIsMobile();
  const backdropRef = useRef<HTMLDivElement>(null);
  /** Panelin açıldığı an — mobilde açılıştan hemen sonraki dokunuşu yok saymak için (aşağıda). */
  const acilisRef = useRef(0);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; left: number; width: number; maxHeight: number }>({ top: 0, left: 0, width: 0, maxHeight: 340 });
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const flat = useMemo<Flat[]>(() => {
    const out: Flat[] = [];
    options.forEach((o) => {
      if (isGroup(o)) {
        out.push({ type: "group", label: o.group });
        o.items.forEach((it) => out.push({ type: "item", ...normalize(it) }));
      } else {
        out.push({ type: "item", ...normalize(o) });
      }
    });
    return out;
  }, [options]);

  // Türkçe katlama: "İptal".toLowerCase() = "i̇ptal" (birleşik nokta) → "iptal" ile EŞLEŞMEZ.
  // NFD + birleşik işaret temizliği + ı→i, arama kutusunun Türkçe adları bulabilmesi için şart.
  const fold = (s: string) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/ı/g, "i");
  const lc = fold(query.trim());
  const filtered = useMemo<Flat[]>(() => {
    if (!lc) return flat;
    const out: Flat[] = [];
    let currentGroup: Flat | null = null;
    let groupHasItems = false;
    for (const f of flat) {
      if (f.type === "group") { currentGroup = f; groupHasItems = false; continue; }
      const hay = fold(f.label + " " + (f.desc || "") + " " + (f.tail || ""));
      if (hay.includes(lc)) {
        if (currentGroup && !groupHasItems) { out.push(currentGroup); groupHasItems = true; }
        out.push(f);
      }
    }
    return out;
  }, [flat, lc]);

  const itemIndices = filtered.reduce<number[]>((acc, f, i) => { if (f.type === "item") acc.push(i); return acc; }, []);
  const activeFilteredIdx = activeIdx >= 0 && activeIdx < itemIndices.length ? itemIndices[activeIdx]! : -1;
  const selectedOpt = flat.find((f): f is FlatItem => f.type === "item" && f.value === value);
  const hasValue = cok ? secililer.size > 0 : selectedOpt != null;
  // Çoklu modda tetikleyici metni: tek seçimde adın kendisi, çoklu seçimde sayı özeti.
  const tetikMetni = cok
    ? (secililer.size === 0 ? placeholder
      : secililer.size === 1
        ? (flat.find((f): f is FlatItem => f.type === "item" && secililer.has(f.value))?.label ?? `1 seçili`)
        : (multiSummary ? multiSummary(secililer.size) : `${secililer.size} seçili`))
    : (hasValue ? selectedOpt!.label : placeholder);

  // Dış tık → kapat (panel body'ye portal'landığı için panelRef de kontrol edilir)
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      // Örtüye dokunuş burada KAPATMAZ: kapatma örtünün kendi onClick'inde. mousedown'da
      // kapatılsaydı örtü DOM'dan hemen düşer, dokunuşun ardından gelen `click` altındaki
      // düğmeye (örn. "Taslak Kaydet") iner ve istenmeyen bir eylem tetiklerdi.
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t) || backdropRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Panel konumu (fixed) — trigger'a göre; scroll/resize'da güncelle.
  // Portal ile body'ye taşınır → tablo/kart overflow:hidden tarafından kırpılmaz.
  useEffect(() => {
    if (!open) return;
    type C = { top?: number; bottom?: number; left: number; width: number; maxHeight: number };
    /** Değer değişmediyse state YAZILMAZ: her kaydırma karesinde yeni nesne kurmak tüm
     *  seçenek listesini (sanallaştırma yok) kare başına yeniden çiziyordu. */
    const yaz = (c: C) => setCoords((p) => (
      p.top === c.top && p.bottom === c.bottom && p.left === c.left && p.width === c.width && p.maxHeight === c.maxHeight ? p : c
    ));
    const update = () => {
      const vw = document.documentElement.clientWidth;
      if (mobil) {
        const vv = window.visualViewport;
        const ust = (vv?.offsetTop ?? 0) + 8;
        const sol = (vv?.offsetLeft ?? 0) + 8;
        const yuk = (vv?.height ?? window.innerHeight) - 16;
        yaz({ top: ust, left: sol, width: (vv?.width ?? vw) - 16, maxHeight: Math.max(200, yuk) });
        return;
      }
      const r = rootRef.current?.getBoundingClientRect();
      if (!r) return;
      const vh = window.innerHeight;
      const below = vh - r.bottom - 8; // trigger altında kalan alan
      const above = r.top - 8; // trigger üstünde kalan alan
      // Ekrana KISTIR: tetikleyici kısmen ekran dışındaysa (yatay kaydırılan tablo hücresi)
      // panel de oraya çiziliyordu ve `fixed` olduğu için ulaşılamıyordu.
      const width = Math.min(r.width, vw - 16);
      const left = Math.min(Math.max(8, r.left), vw - width - 8);
      // Altta yeterli yer varsa (ya da üstten fazlaysa) AŞAĞI aç; değilse YUKARI aç → viewport dışına taşıp kırpılmaz.
      if (below >= 220 || below >= above) {
        yaz({ top: r.bottom + 4, left, width, maxHeight: Math.max(160, Math.min(340, below)) });
      } else {
        yaz({ bottom: vh - r.top + 4, left, width, maxHeight: Math.max(160, Math.min(340, above)) });
      }
    };
    /** Listenin KENDİ kaydırması konumu değiştirmez — yakalama aşamasında gelir, atlanır. */
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && panelRef.current?.contains(e.target)) return;
      update();
    };
    update();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", update);
    // Klavye aç/kapa ve iOS'un görünür alanı kaydırması YALNIZ visualViewport'tan duyulur.
    const vv = window.visualViewport;
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", update);
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
    };
  }, [open, mobil]);

  // Açılışta arama odağı + seçili öğeye konum. Dış filtre (seedQuery) varsa arama kutusuna
  // önceden yazılır → liste o terimle daralmış açılır; kullanıcı kutuda revize/temizleyebilir.
  useEffect(() => {
    if (open) {
      acilisRef.current = Date.now();
      setQuery(seedQuery?.trim() ? seedQuery : "");
      setTimeout(() => searchRef.current?.focus(), 10);
      const sel = flat.findIndex((f) => f.type === "item" && f.value === value);
      if (sel >= 0) setActiveIdx(flat.slice(0, sel + 1).filter((f) => f.type === "item").length - 1);
      else setActiveIdx(0);
    } else {
      setQuery("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Aktif öğeyi görünür tut
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-fidx="${activeFilteredIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeFilteredIdx, open]);

  function selectItem(item: FlatItem) {
    /**
     * HAYALET DOKUNUŞ KORUMASI (mobil). Panel mobilde tetikleyicinin olduğu yere değil ekranın
     * ÜSTÜNE açılır. Tetikleyiciye hızlı ikinci dokunuş, yeni açılan listede parmağın altına denk
     * gelen seçeneği seçiyordu. Açılıştan sonraki ilk 350 ms'deki seçim yok sayılır.
     */
    if (mobil && Date.now() - acilisRef.current < 350) return;
    if (cok) {
      // Çoklu modda panel AÇIK kalır — kullanıcı arka arkaya birkaç müşteri işaretler.
      const sonraki = new Set(secililer);
      if (sonraki.has(item.value)) sonraki.delete(item.value); else sonraki.add(item.value);
      onChangeMulti?.([...sonraki]);
      return;
    }
    onChange(item.value);
    setOpen(false);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(itemIndices.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const fi = itemIndices[activeIdx];
      const f = fi != null ? filtered[fi] : null;
      if (f && f.type === "item") selectItem(f);
    } else if (e.key === "Escape") {
      // Yayılımı KES: aksi halde açık listeyi kapatan ESC üstteki drawer'a ulaşıp onu da kapatır.
      e.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <div className="ss-root" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`ss-trigger ${open ? "open" : ""} ${error ? "error" : ""}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {icon ? <span className="ss-trig-icon"><Icon name={icon} size={14} /></span> : null}
        <span className={`ss-trig-text ${!hasValue ? "placeholder" : ""}`}>{tetikMetni}</span>
        {!cok && hasValue && selectedOpt!.tail ? <span className="ss-trig-meta">{selectedOpt!.tail}</span> : null}
        {clearable && hasValue && !disabled ? (
          <span role="button" tabIndex={-1} className="ss-clear-btn" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (cok) onChangeMulti?.([]); else onChange(null); }} aria-label="Temizle">
            <Icon name="x" size={12} />
          </span>
        ) : null}
        <span className="ss-trig-chev"><Icon name="chevDown" size={14} /></span>
      </button>

      {/* Panel PASİF SEKMEDE basılmaz: portal `document.body`ye gider, sekme panelinin
          gizlenmesi ona ulaşmaz — açık bırakılan bir arama paneli aktif sayfanın üstünde
          asılı kalırdı. Seçim durumu bileşenin state'inde yaşamaya devam eder. */}
      {open && tabActive ? createPortal(
        <>
        {/* Mobilde örtü: panel ekranın üstünü kaplar; altındaki sayfa kararır ve dokunuş
            ALTTAKİ düğmeye geçmez (dışa dokunmak yalnız kapatır). */}
        {mobil ? <div className="ss-backdrop" ref={backdropRef} onClick={() => setOpen(false)} aria-hidden="true" /> : null}
        <div className={`ss-panel${mobil ? " ss-mobile" : ""}`} ref={panelRef} role="listbox" style={{ top: coords.top, bottom: coords.bottom, left: coords.left, width: coords.width, maxHeight: coords.maxHeight }}>
          <div className="ss-search">
            <Icon name="search" size={13} />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
              onKeyDown={onKey}
              placeholder={searchPlaceholder}
              autoComplete="off"
              spellCheck={false}
            />
            {query ? (
              <button type="button" className="ss-search-clear" onClick={() => { setQuery(""); searchRef.current?.focus(); }} aria-label="Aramayı temizle">
                <Icon name="x" size={12} />
              </button>
            ) : null}
            {/* Mobilde AÇIK kapatma: panel ekranın neredeyse tamamını kaplar, dışarıda yalnız 8px'lik
                örtü şeridi kalır. Çoklu seçimde panel seçimle kapanmadığı için başka çıkış yolu HİÇ
                yoktu. Çoklu modda "Tamam" (seçimler zaten uygulanmış), tekli modda "Kapat". */}
            {mobil ? (
              <button type="button" className="ss-mobile-close" onClick={() => setOpen(false)}>
                {cok ? "Tamam" : "Kapat"}
              </button>
            ) : null}
          </div>

          <div className="ss-list" ref={listRef}>
            {filtered.filter((f) => f.type === "item").length === 0 ? (
              <div className="ss-empty">
                <span className="ss-empty-ic"><Icon name="search" size={20} /></span>
                <span>{emptyText}</span>
                {query ? <span style={{ fontSize: 11, color: "var(--neutral-400)" }}>"{query}" için sonuç yok</span> : null}
              </div>
            ) : null}
            {filtered.map((f, fi) => {
              if (f.type === "group") return <div className="ss-group" key={`g-${fi}`}>{f.label}</div>;
              const selected = cok ? secililer.has(f.value) : f.value === value;
              const active = fi === activeFilteredIdx;
              return (
                <button
                  type="button"
                  key={`${f.value}-${fi}`}
                  data-fidx={fi}
                  className={`ss-item ${selected ? "selected" : ""} ${active ? "active" : ""}`}
                  onMouseEnter={() => setActiveIdx(filtered.slice(0, fi + 1).filter((x) => x.type === "item").length - 1)}
                  // ÇOKLU modda panel açık kalır: tıklama odağı arama kutusundan ALMAMALI,
                  // yoksa sonraki ↑↓/↵ tuşları input'un onKeyDown'ına hiç ulaşmaz.
                  onMouseDown={cok ? (e) => e.preventDefault() : undefined}
                  onClick={() => selectItem(f)}
                  role="option"
                  aria-selected={selected}
                >
                  {f.icon ? <span className="ss-item-icon"><Icon name={f.icon} size={14} /></span> : null}
                  <span className="ss-item-text">
                    <span className="ss-item-lbl">{f.label}</span>
                    {f.desc ? <span className="ss-item-desc">{f.desc}</span> : null}
                  </span>
                  {f.tail ? <span className="ss-item-tail">{f.tail}</span> : null}
                  {itemAction ? (
                    <span
                      role="button"
                      tabIndex={-1}
                      className="ss-item-act"
                      aria-label={itemAction.label}
                      title={itemAction.label}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); itemAction.onAction(f.value); setOpen(false); }}
                    >
                      <Icon name={itemAction.icon} size={13} />
                    </span>
                  ) : null}
                  <span className="ss-item-check"><Icon name="check" size={14} /></span>
                </button>
              );
            })}
          </div>

          {showFooter ? (
            <div className="ss-foot">
              <span>{filtered.filter((f) => f.type === "item").length} sonuç{cok && secililer.size > 0 ? ` · ${secililer.size} seçili` : ""}</span>
              <span className="ss-foot-keys"><kbd>↑↓</kbd> <kbd>↵</kbd> <kbd>esc</kbd></span>
            </div>
          ) : null}
        </div>
        </>,
        document.body
      ) : null}
    </div>
  );
}
